import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Checkbox,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, type Theme, useTheme } from "@mui/material/styles";
import { CollapseChevron } from "components/CollapseChevron";
import { type BulkManifestDiscrepanciesQuery } from "generated-graphql/graphql";
import { useState } from "react";

import { DataSourceConflictRowExpander } from "./DataSourceConflictRowExpander";
import { describeWasteLine, type FieldGroup, getColCount } from "./DataSourceConflictsPane.helpers";
import {
  COLUMN_HEADER_SX,
  formatFieldValue,
  getDiscrepancyStatus,
} from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancyStatusChip } from "./DiscrepancyStatusChip";
import { discrepancyTableSx, groupHeaderFill } from "./discrepancyTableSx";
import {
  type EManifestBox18a,
  EManifestBox18aCaption,
  getConfirmedBox18aAmount,
} from "./EManifestBox18aCaption";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

function SectionHeaderRow({
  label,
  count,
  colSpan,
  collapsed,
  onToggle,
  heldBackReason,
}: {
  label: string;
  count: number;
  colSpan: number;
  collapsed: boolean;
  onToggle: () => void;
  heldBackReason?: string | null;
}) {
  const theme = useTheme();
  const isHeldBack = heldBackReason != null;
  const groupFill = groupHeaderFill(theme);
  return (
    <TableRow
      onClick={onToggle}
      sx={{
        cursor: "pointer",
        "&:hover": { bgcolor: groupFill.hover },
      }}
    >
      <TableCell
        colSpan={colSpan}
        sx={{
          bgcolor: groupFill.bg,
          py: 1,
          px: 0,
          fontWeight: 600,
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Inner sticky wrapper pins the label + chevron to the left edge during horizontal scroll. */}
        <Box
          sx={{
            position: "sticky",
            left: 0,
            width: "fit-content",
            px: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CollapseChevron collapsed={collapsed} />
            <span>{label}</span>
            <Typography variant="caption" color="text.secondary" component="span">
              ({count})
            </Typography>
            {isHeldBack && (
              // Held back = a warning state, not an aside — warning.dark over warning.main:
              // ~3.5:1 vs ~2.9:1 on this header's light-mode background (the tighter case).
              <Typography variant="caption" sx={{ color: "warning.dark" }}>
                — {heldBackReason}
              </Typography>
            )}
          </Stack>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function DataSourceConflictsHeader({ showManifestColumns }: { showManifestColumns: boolean }) {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" />
        <TableCell padding="checkbox" />
        {showManifestColumns && (
          <>
            <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 160 }}>Facility</TableCell>
            <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 140 }}>Manifest</TableCell>
          </>
        )}
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 200 }}>Waste line</TableCell>
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 90 }}>Document</TableCell>
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 200 }}>e-Manifest</TableCell>
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 200 }}>Vendor</TableCell>
        <TableCell data-status-cell sx={{ ...COLUMN_HEADER_SX, minWidth: 100 }}>
          Status
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

function DataSourceConflictsLoadingSkeleton({
  showManifestColumns,
  colCount,
}: {
  showManifestColumns: boolean;
  colCount: number;
}) {
  const headers = [
    "",
    "",
    ...(showManifestColumns ? ["Facility", "Manifest"] : []),
    "Waste line",
    "e-Manifest",
    "Vendor",
    "Document",
    "Status",
  ];
  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map((h, i) => (
              <TableCell key={h || `sk-${i}`}>
                <Skeleton variant="text" width={80} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 8 }, (_, i) => (
            <TableRow key={i}>
              {Array.from({ length: colCount }, (__, j) => (
                <TableCell key={j}>
                  <Skeleton variant="text" width={j === 2 ? 120 : 80} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}
function ChevronCell({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <TableCell data-icon-cell="chevron">
      <Box data-row-icon>
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={open ? "Collapse row" : "Expand row"}
        >
          <CollapseChevron collapsed={!open} />
        </IconButton>
      </Box>
    </TableCell>
  );
}

type ToggleSelect = (id: string, opts?: { shiftKey?: boolean }) => void;

function SelectionCell({
  discrepancy,
  selected,
  onToggleSelect,
}: {
  discrepancy: Discrepancy;
  selected: boolean;
  onToggleSelect: ToggleSelect;
}) {
  return (
    <TableCell data-icon-cell="selection">
      <Box data-row-icon>
        <Checkbox
          size="small"
          checked={selected}
          onChange={(e) => {
            // shiftKey lives on the native event, not the synthetic ChangeEvent.
            const nativeEvt = e.nativeEvent;
            const shiftKey =
              nativeEvt instanceof MouseEvent || nativeEvt instanceof KeyboardEvent
                ? nativeEvt.shiftKey
                : false;
            onToggleSelect(discrepancy.id, { shiftKey });
          }}
        />
      </Box>
    </TableCell>
  );
}

function FacilityCell({ discrepancy }: { discrepancy: Discrepancy }) {
  return (
    <TableCell sx={{ whiteSpace: "nowrap" }}>
      <Typography variant="body2">{discrepancy.facilityName ?? "—"}</Typography>
      <Typography variant="caption" color="text.secondary">
        {discrepancy.epaId}
      </Typography>
    </TableCell>
  );
}

function ManifestCell({ discrepancy }: { discrepancy: Discrepancy }) {
  return (
    <TableCell sx={{ whiteSpace: "nowrap" }}>
      <Typography variant="body2" fontWeight={500}>
        {discrepancy.manifestTrackingNumber ?? discrepancy.processedManifestId.slice(0, 8)}
      </Typography>
    </TableCell>
  );
}

function WasteLineCell({ discrepancy }: { discrepancy: Discrepancy }) {
  const { primary, secondary } = describeWasteLine(discrepancy);
  return (
    // wordBreak, not nowrap: secondary carries the waste line's shipping description,
    // which can run 60+ characters — unlike the tracking number this cell replaced.
    // Primary and secondary flow as one wrapping paragraph rather than stacked
    // blocks, so a short row (e.g. "Manifest Box 9b") reads on a single line.
    <TableCell sx={{ maxWidth: 260, wordBreak: "break-word" }}>
      <Typography variant="body2">
        <span>{primary}</span>
        {secondary && (
          <Typography component="span" variant="caption" color="text.secondary">
            {" "}
            {secondary}
          </Typography>
        )}
      </Typography>
    </TableCell>
  );
}

function ConflictValueCell({
  value,
  field,
  box18a,
  confirmedAmount,
}: {
  value: string | null | undefined;
  field: string;
  box18a?: EManifestBox18a | null;
  confirmedAmount?: string | null;
}) {
  const display = formatFieldValue(field, value);
  return (
    <TableCell sx={{ maxWidth: 240, wordBreak: "break-word" }}>
      <Typography variant="body2" fontWeight={600}>
        {display}
      </Typography>
      <EManifestBox18aCaption box18a={box18a} confirmedAmount={confirmedAmount} />
    </TableCell>
  );
}

function ConflictDocumentCell({
  discrepancy,
  isPreviewedDoc,
  onViewDocument,
}: {
  discrepancy: Discrepancy;
  isPreviewedDoc: boolean;
  onViewDocument: (documentId: string, fileExtension: string | null | undefined) => void;
}) {
  return (
    <TableCell sx={{ maxWidth: 240, wordBreak: "break-word" }}>
      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
        {discrepancy.sourceDocumentId && (
          <Box data-row-icon>
            <Tooltip title={isPreviewedDoc ? "Viewing document" : "Preview source document"}>
              <IconButton
                size="small"
                sx={{ pl: 0 }}
                onClick={() => {
                  if (discrepancy.sourceDocumentId) {
                    onViewDocument(
                      discrepancy.sourceDocumentId,
                      discrepancy.sourceDocumentFileExtension
                    );
                  }
                }}
              >
                {isPreviewedDoc ? (
                  <VisibilityIcon sx={{ fontSize: 16 }} color="primary" />
                ) : (
                  <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {discrepancy.field !== "wasteCodeDotConsistency" && (
          <Typography variant="body2" fontWeight={600}>
            {formatFieldValue(discrepancy.field, discrepancy.manifestDocumentValue)}
          </Typography>
        )}
      </Stack>
    </TableCell>
  );
}
function getRowBackground({
  theme,
  selected,
  isPreviewedDoc,
  rowDividerColor,
}: {
  theme: Theme;
  selected: boolean;
  isPreviewedDoc: boolean;
  rowDividerColor: string;
}): { baseBgcolor: string | undefined; hoverBgcolor: string; dividerColor: string } {
  // Row background precedence: selected > previewed doc > default. Divider tracks the background.
  if (selected) {
    return {
      baseBgcolor: alpha(theme.palette.primary.main, 0.2),
      hoverBgcolor: alpha(theme.palette.primary.main, 0.24),
      dividerColor: alpha(theme.palette.primary.main, 0.1),
    };
  }
  if (isPreviewedDoc) {
    return {
      baseBgcolor: alpha(theme.palette.primary.main, 0.08),
      hoverBgcolor: alpha(theme.palette.primary.main, 0.12),
      dividerColor: alpha(theme.palette.primary.main, 0.1),
    };
  }
  return {
    baseBgcolor: undefined,
    hoverBgcolor: theme.palette.action.hover,
    dividerColor: rowDividerColor,
  };
}

function ConflictRow({
  discrepancy,
  selected,
  onToggleSelect,
  showManifestColumns,
  activePreviewDocId,
  onViewDocument,
  open,
  onToggleOpen,
  colCount,
  isStaff,
  onResolve,
  onMarkUnresolved,
  onUndoResolution,
  mutationLoading,
  rowDividerColor,
}: {
  discrepancy: Discrepancy;
  selected: boolean;
  onToggleSelect: ToggleSelect;
  showManifestColumns: boolean;
  activePreviewDocId: string | null;
  onViewDocument: (documentId: string, fileExtension: string | null | undefined) => void;
  open: boolean;
  onToggleOpen: () => void;
  colCount: number;
  /** Every row action in the expander is staff-only; passed straight through. */
  isStaff: boolean;
  onResolve: (discrepancy: Discrepancy) => void;
  onMarkUnresolved: (discrepancy: Discrepancy) => void;
  onUndoResolution: (discrepancy: Discrepancy) => void;
  /** True while any resolve mutation is in flight; disables the expander's actions. */
  mutationLoading?: boolean;
  rowDividerColor: string;
}) {
  const theme = useTheme();
  const status = getDiscrepancyStatus(discrepancy);
  const isPreviewedDoc =
    activePreviewDocId != null &&
    discrepancy.sourceDocumentId != null &&
    discrepancy.sourceDocumentId === activePreviewDocId;
  const { baseBgcolor, hoverBgcolor, dividerColor } = getRowBackground({
    theme,
    selected,
    isPreviewedDoc,
    rowDividerColor,
  });

  return (
    <>
      <TableRow
        data-expanded={open ? "true" : undefined}
        selected={selected}
        onMouseDown={(event) => {
          // Suppress native shift-click text-selection across rows; non-shift selection still works.
          if (event.shiftKey) {
            event.preventDefault();
          }
        }}
        onClick={(event) => {
          // Let interactive descendants handle their own clicks before row selection.
          if (
            event.target instanceof Element &&
            event.target.closest('button, a, input, [role="button"]')
          ) {
            return;
          }
          // A click that ends a text-selection drag is a copy gesture, not a
          // row-select intent — consuming it would both toggle the row and
          // make text feel unselectable.
          if (window.getSelection()?.toString()) {
            return;
          }
          onToggleSelect(discrepancy.id, { shiftKey: event.shiftKey });
        }}
        sx={{
          cursor: "pointer",
          "& .MuiTableCell-body": {
            borderBottomColor: dividerColor,
          },
          ...(baseBgcolor != null ? { bgcolor: baseBgcolor } : {}),
          // Override MUI's Mui-selected tinting so primary wins over action.selected.
          ...(selected
            ? {
                "&.Mui-selected": { bgcolor: baseBgcolor },
                "&.Mui-selected:hover": { bgcolor: hoverBgcolor },
              }
            : {}),
          "&:hover": {
            bgcolor: hoverBgcolor,
          },
        }}
      >
        <ChevronCell open={open} onToggle={onToggleOpen} />
        <SelectionCell
          discrepancy={discrepancy}
          selected={selected}
          onToggleSelect={onToggleSelect}
        />
        {showManifestColumns && (
          <>
            <FacilityCell discrepancy={discrepancy} />
            <ManifestCell discrepancy={discrepancy} />
          </>
        )}
        <WasteLineCell discrepancy={discrepancy} />

        {/* Sources run Document, e-Manifest, Vendor. The document column always
            renders so its preview eyeball is there on every row. */}
        <ConflictDocumentCell
          discrepancy={discrepancy}
          isPreviewedDoc={isPreviewedDoc}
          onViewDocument={onViewDocument}
        />

        {discrepancy.field === "wasteCodeDotConsistency" ? (
          // Within-line consistency flag: not a three-way value diff. Render the
          // synthesized "needs review" message across the eManifest + vendor
          // columns only (colSpan 2) — the document column below still renders so
          // its source-document preview eyeball stays available for verifying box 9.
          <TableCell colSpan={2} sx={{ wordBreak: "break-word" }}>
            <Typography variant="body2">
              {discrepancy.eManifestValue ?? "Waste code / DOT description mismatch — needs review"}
            </Typography>
          </TableCell>
        ) : (
          <>
            <ConflictValueCell
              value={discrepancy.eManifestValue}
              field={discrepancy.field}
              box18a={discrepancy.eManifestBox18a}
              confirmedAmount={getConfirmedBox18aAmount(discrepancy)}
            />
            <ConflictValueCell value={discrepancy.vendorValue} field={discrepancy.field} />
          </>
        )}

        <TableCell data-status-cell>
          <DiscrepancyStatusChip status={status} />
        </TableCell>
      </TableRow>

      {open && (
        <DataSourceConflictRowExpander
          discrepancy={discrepancy}
          status={status}
          open={open}
          colSpan={colCount}
          isStaff={isStaff}
          onResolve={onResolve}
          onMarkUnresolved={onMarkUnresolved}
          onUndoResolution={onUndoResolution}
          mutationLoading={mutationLoading}
        />
      )}
    </>
  );
}

export interface DataSourceConflictsPaneProps {
  /**
   * The field groups, in display order, built by the parent. `useRangeSelection`
   * resolves a shift-click range by index and has to index the order rendered
   * here, so one caller owns the grouping and the sort.
   */
  fieldGroups: FieldGroup[];
  loading?: boolean;
  /** Selection set shared with the dialog's sticky bulk-action bar. */
  selectedIds: ReadonlySet<string>;
  /** Toggle a row's selection; shiftKey enables range-select. */
  onToggleSelect: ToggleSelect;
  /** False when the dialog is scoped to a single manifest, which already names it in the header. */
  showManifestColumns: boolean;
  activePreviewDocId: string | null;
  onViewDocument: (documentId: string, fileExtension: string | null | undefined) => void;
  /** Every row action in the expander is staff-only; a non-staff viewer gets no button row. */
  isStaff: boolean;
  onResolve: (discrepancy: Discrepancy) => void;
  onMarkUnresolved: (discrepancy: Discrepancy) => void;
  onUndoResolution: (discrepancy: Discrepancy) => void;
  mutationLoading?: boolean;
  /** Rendered above the table — e.g. a future misaligned-waste-lines warning. This commit only adds the slot. */
  banner?: React.ReactNode;
}

/**
 * Data Source Conflicts as a table grouped by field, one row per finding
 * comparing e-Manifest, Vendor, and the uploaded manifest document. Each
 * row's chevron opens the full comparison, per-axis verdicts, resolve
 * actions, and event history.
 */
export function DataSourceConflictsPane({
  fieldGroups,
  loading = false,
  selectedIds,
  onToggleSelect,
  showManifestColumns,
  activePreviewDocId,
  onViewDocument,
  isStaff,
  onResolve,
  onMarkUnresolved,
  onUndoResolution,
  banner,
  mutationLoading,
}: DataSourceConflictsPaneProps) {
  const theme = useTheme();
  const rowDividerColor = theme.palette.divider;

  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggleOpen = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Which groups the reader has toggled away from how they arrived, rather
  // than which are collapsed: a held-back group arrives folded and the rest
  // arrive open, so one set cannot mean "collapsed" for both.
  const [toggledGroups, setToggledGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (field: string) => {
    setToggledGroups((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const colCount = getColCount(showManifestColumns);

  if (loading && fieldGroups.length === 0) {
    return (
      <DataSourceConflictsLoadingSkeleton
        showManifestColumns={showManifestColumns}
        colCount={colCount}
      />
    );
  }

  return (
    <>
      {banner && <Box sx={{ px: 2, pb: 1 }}>{banner}</Box>}
      {/* White grid surface so rows don't inherit the card's gray; the grid is the
          card's last element, so round its bottom to match the card. */}
      <TableContainer
        sx={{
          overflow: "visible",
          bgcolor: "background.paper",
          borderBottomLeftRadius: "inherit",
          borderBottomRightRadius: "inherit",
        }}
      >
        <Table
          size="small"
          sx={{
            ...discrepancyTableSx(theme),
          }}
        >
          <DataSourceConflictsHeader showManifestColumns={showManifestColumns} />
          {fieldGroups.map((group) => {
            // A held-back group arrives folded: its rows are the ones a
            // reviewer cannot act on yet, so the reason in the header is what
            // they need before the values. Opening it still has to work, or
            // the rows are unreachable and the fold is a deletion.
            const isHeldBack = group.heldBackReason != null;
            const toggled = toggledGroups.has(group.field);
            const collapsed = toggled ? !isHeldBack : isHeldBack;
            return (
              <TableBody key={group.field}>
                <SectionHeaderRow
                  label={group.label}
                  count={group.items.length}
                  colSpan={colCount}
                  collapsed={collapsed}
                  onToggle={() => toggleGroup(group.field)}
                  heldBackReason={group.heldBackReason}
                />
                {!collapsed &&
                  group.items.map((d) => (
                    <ConflictRow
                      key={d.id}
                      discrepancy={d}
                      selected={selectedIds.has(d.id)}
                      onToggleSelect={onToggleSelect}
                      showManifestColumns={showManifestColumns}
                      activePreviewDocId={activePreviewDocId}
                      onViewDocument={onViewDocument}
                      open={openIds.has(d.id)}
                      onToggleOpen={() => toggleOpen(d.id)}
                      colCount={colCount}
                      isStaff={isStaff}
                      onResolve={onResolve}
                      onMarkUnresolved={onMarkUnresolved}
                      onUndoResolution={onUndoResolution}
                      mutationLoading={mutationLoading}
                      rowDividerColor={rowDividerColor}
                    />
                  ))}
              </TableBody>
            );
          })}
        </Table>
      </TableContainer>
    </>
  );
}
